import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ShareSheet } from '../components/ShareButton';

// Lecteur de cartes de révision plein écran, façon Reels :
// une carte par écran, on glisse vers le haut / le bas pour passer d'une carte à l'autre.
// - tap sur une carte : affiche / cache la réponse (ou met la vidéo en pause)
// - double tap : « je sais » (comme le ❤ d'Instagram)
// - clavier : ↑ ↓ pour naviguer, espace pour la réponse, M pour le son, Échap pour quitter

const DOUBLE_TAP_MS = 260;

const Icon = {
  close: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" /></svg>,
  check: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  soundOn: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="currentColor" /><path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>,
  soundOff: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="currentColor" /><path d="M16 9.5l5 5M21 9.5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>,
  share: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 3L10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" /></svg>,
  play: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" fill="currentColor" /></svg>,
  up: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 15l6-6 6 6" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  down: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  heart: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 7.9 3.7 4.5 7.1 4.5c2 0 3.5 1.1 4.9 2.9 1.4-1.8 2.9-2.9 4.9-2.9 3.4 0 5.7 3.4 4.4 6.8-1.8 4.6-9.3 9.2-9.3 9.2Z" fill="currentColor" /></svg>,
};

const storageKey = (deckId) => `cards-known-${deckId}`;
const loadKnown = (deckId) => {
  try { const v = JSON.parse(localStorage.getItem(storageKey(deckId)) || '[]'); return new Set(Array.isArray(v) ? v : []); }
  catch { return new Set(); }
};
const saveKnown = (deckId, set) => { try { localStorage.setItem(storageKey(deckId), JSON.stringify([...set])); } catch {} };

const textSize = (t) => (t.length <= 70 ? 'xl' : t.length <= 180 ? 'l' : t.length <= 420 ? 'm' : 's');
const hashIndex = () => { const m = /^#c=(\d+)$/.exec(window.location.hash); return m ? Math.max(0, Number(m[1]) - 1) : 0; };

// Légende repliée sur 3 lignes, « plus » pour tout lire
function Caption({ text }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > 140 || text.split('\n').length > 3;
  return (
    <p className={`r-caption ${open ? 'open' : ''}`} onClick={(e) => { if (long) { e.stopPropagation(); setOpen(o => !o); } }}>
      {text}
      {long && !open && <span className="r-caption-more">… plus</span>}
    </p>
  );
}

function Slide({ card, index, total, active, near, revealed, known, muted, paused, burst, videoRef, onTap, onReveal, onKnown, onMute, onShare, onVideoState }) {
  const [progress, setProgress] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const isVideo = card.media_type === 'video';
  const isImage = card.media_type === 'image';
  const textOnly = card.media_type === 'none';

  return (
    <section className="r-slide" data-index={index} aria-roledescription="carte" aria-label={`Carte ${index + 1} sur ${total}`}>
      <div className={`r-card ${textOnly ? `r-text-card tone-${index % 4}` : ''} ${revealed ? 'is-revealed' : ''}`} onClick={onTap}>
        {isImage && (
          <>
            <div className="r-backdrop" style={{ backgroundImage: `url("${card.media_url}")` }} aria-hidden="true" />
            <img className="r-media" src={card.media_url} alt="" loading={near ? 'eager' : 'lazy'} draggable="false" />
          </>
        )}
        {isVideo && (
          <>
            {near && !videoError ? (
              <video
                ref={videoRef}
                className="r-media"
                src={card.media_url}
                muted={muted}
                loop
                playsInline
                preload={active ? 'auto' : 'metadata'}
                onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setProgress(v.currentTime / v.duration); }}
                onPlay={() => onVideoState(false)}
                onPause={() => onVideoState(true)}
                onError={() => setVideoError(true)}
              />
            ) : videoError ? <div className="r-media-error">Vidéo indisponible pour le moment</div> : null}
            {active && paused && !videoError && <span className="r-paused" aria-hidden="true">{Icon.play}</span>}
            <span className="r-video-bar" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></span>
          </>
        )}
        {textOnly && <p className={`r-text r-text-${textSize(card.front)}`}>{card.front}</p>}

        {!textOnly && (
          <div className="r-bottom">
            <Caption text={card.front} />
          </div>
        )}

        {card.back && (
          <button type="button" className="r-reveal" onClick={(e) => { e.stopPropagation(); onReveal(); }} aria-expanded={revealed}>
            {revealed ? 'cacher la réponse' : 'voir la réponse'}
          </button>
        )}

        {card.back && (
          <div className="r-answer" aria-hidden={!revealed} onClick={(e) => { e.stopPropagation(); onReveal(); }}>
            <span className="r-answer-label">réponse</span>
            <p className={`r-answer-text r-text-${textSize(card.back)}`}>{card.back}</p>
            <span className="r-answer-hint">touchez pour revenir à la carte</span>
          </div>
        )}

        {burst && <span key={burst} className="r-burst" aria-hidden="true">{Icon.heart}</span>}
      </div>

      <div className="r-rail">
        <button type="button" className={`r-rail-btn ${known ? 'on' : ''}`} onClick={onKnown} aria-pressed={known}>
          <span className="r-rail-icon">{Icon.check}</span>
          <span className="r-rail-label">{known ? 'je sais' : 'je sais ?'}</span>
        </button>
        {isVideo && (
          <button type="button" className="r-rail-btn" onClick={onMute} aria-pressed={!muted}>
            <span className="r-rail-icon">{muted ? Icon.soundOff : Icon.soundOn}</span>
            <span className="r-rail-label">{muted ? 'son' : 'muet'}</span>
          </button>
        )}
        <button type="button" className="r-rail-btn" onClick={onShare}>
          <span className="r-rail-icon">{Icon.share}</span>
          <span className="r-rail-label">partager</span>
        </button>
      </div>
    </section>
  );
}

export default function CardsPlay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [list, setList] = useState([]);        // cartes affichées (toutes, ou seulement celles à revoir)
  const [reviewMode, setReviewMode] = useState(false);
  const [active, setActive] = useState(0);
  const [revealed, setRevealed] = useState(() => new Set());
  const [known, setKnown] = useState(() => new Set());
  const [muted, setMuted] = useState(true);    // autoplay mobile : la vidéo doit démarrer sans le son
  const [paused, setPaused] = useState(false);
  const [bursts, setBursts] = useState({});
  const [hint, setHint] = useState(true);
  const [sharing, setSharing] = useState(null); // { url, title, text }
  const feedRef = useRef(null);
  const videoRefs = useRef({});
  const tapTimer = useRef(null);
  const startIndex = useRef(hashIndex());

  useEffect(() => {
    fetch(`/api/cards/decks/${id}`)
      .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'Ce paquet n\'existe pas ou n\'est plus publié.' : 'Chargement impossible.'); return r.json(); })
      .then(d => { setDeck(d); setList(d.cards); setKnown(loadKnown(d.id)); document.title = `${d.title} — Cards · Zahra's Learning`; })
      .catch(e => setLoadError(e.message || 'Chargement impossible.'));
    return () => { document.title = "Zahra's Learning — Fatima-Zahra Moussaid"; };
  }, [id]);

  // la page ne doit pas « rebondir » derrière le lecteur
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const total = list.length;
  const slideCount = total + 1; // + l'écran de fin

  const goTo = useCallback((i, smooth = true) => {
    const feed = feedRef.current;
    if (!feed) return;
    const n = Math.max(0, Math.min(slideCount - 1, i));
    feed.scrollTo({ top: n * feed.clientHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, [slideCount]);

  // ouverture directe sur une carte (lien partagé : #c=5)
  useEffect(() => {
    if (!deck) return;
    const i = Math.min(startIndex.current, Math.max(0, deck.cards.length - 1));
    if (i > 0) { requestAnimationFrame(() => goTo(i, false)); setActive(i); setHint(false); }
  }, [deck, goTo]);

  // lien vers une autre carte du même paquet ouvert dans la même page
  useEffect(() => {
    const onHash = () => goTo(hashIndex(), false);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [goTo]);

  const onScroll = () => {
    const feed = feedRef.current;
    if (!feed) return;
    const i = Math.round(feed.scrollTop / feed.clientHeight);
    if (i !== active) setActive(i);
    if (feed.scrollTop > 20 && hint) setHint(false);
  };

  // lecture automatique de la vidéo de la carte affichée, pause des autres
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([k, v]) => {
      if (!v) return;
      if (Number(k) === active) {
        v.muted = muted;
        const p = v.play();
        if (p && p.catch) p.catch(() => setPaused(true));
      } else if (!v.paused || v.currentTime) {
        v.pause();
        try { v.currentTime = 0; } catch {}
      }
    });
  }, [active, muted, list]);

  useEffect(() => {
    setPaused(false);
    if (deck && active < total) window.history.replaceState(null, '', `#c=${active + 1}`);
  }, [active, deck, total]);

  const toggleReveal = (card) => setRevealed(prev => {
    const next = new Set(prev);
    next.has(card.id) ? next.delete(card.id) : next.add(card.id);
    return next;
  });

  const setKnownCard = (card, value) => setKnown(prev => {
    const next = new Set(prev);
    value ? next.add(card.id) : next.delete(card.id);
    saveKnown(deck.id, next);
    return next;
  });

  const togglePlay = (i) => {
    const v = videoRefs.current[i];
    if (!v) return;
    if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause();
  };

  // un tap = réponse (ou pause pour une vidéo) ; deux taps rapides = « je sais » + cœur
  const onTap = (card, i) => {
    if (tapTimer.current) {
      clearTimeout(tapTimer.current); tapTimer.current = null;
      setKnownCard(card, true);
      setBursts(b => ({ ...b, [card.id]: Date.now() }));
      try { if (navigator.vibrate) navigator.vibrate(15); } catch {}
      return;
    }
    tapTimer.current = setTimeout(() => {
      tapTimer.current = null;
      if (card.media_type === 'video') togglePlay(i);
      else if (card.back) toggleReveal(card);
    }, DOUBLE_TAP_MS);
  };

  const share = (card, i) => setSharing({
    url: `${window.location.origin}/cards/${deck.id}#c=${i + 1}`,
    title: `${deck.title} — carte ${i + 1}`,
    text: card.front ? `${card.front.slice(0, 120)}${card.front.length > 120 ? '…' : ''} — cartes de révision « ${deck.title} »` : `Cartes de révision « ${deck.title} »`,
  });
  const shareDeck = () => setSharing({
    url: `${window.location.origin}/cards/${deck.id}`,
    title: `${deck.title} — cartes de révision`,
    text: `${knownCount}/${deck.cards.length} cartes sues sur le paquet « ${deck.title} » ✦ À toi de réviser !`,
  });

  const restart = (onlyToReview) => {
    const cards = onlyToReview ? deck.cards.filter(c => !known.has(c.id)) : deck.cards;
    setReviewMode(onlyToReview);
    setRevealed(new Set());
    setList(cards);
    setActive(0);
    requestAnimationFrame(() => goTo(0, false));
  };

  // molette / pavé tactile (ordinateur) : un geste = une carte, comme un swipe
  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return undefined;
    let acc = 0; let lockedUntil = 0;
    const onWheel = (e) => {
      if (e.ctrlKey || Math.abs(e.deltaY) < Math.abs(e.deltaX)) return; // zoom ou geste horizontal
      if (e.target.closest && e.target.closest('.r-caption.open, .r-answer-text, .r-text')) {
        const el = e.target.closest('.r-caption.open, .r-answer-text, .r-text');
        if (el.scrollHeight > el.clientHeight + 2) return; // texte long : on le laisse défiler
      }
      e.preventDefault();
      const now = Date.now();
      if (now < lockedUntil) return;
      acc += e.deltaMode === 1 ? e.deltaY * 30 : e.deltaY;
      if (Math.abs(acc) >= 40) {
        const i = Math.round(feed.scrollTop / feed.clientHeight);
        goTo(i + (acc > 0 ? 1 : -1));
        acc = 0; lockedUntil = now + 650;
      }
    };
    feed.addEventListener('wheel', onWheel, { passive: false });
    return () => feed.removeEventListener('wheel', onWheel);
  }, [deck, goTo]);

  // clavier (ordinateur)
  useEffect(() => {
    const onKey = (e) => {
      if (sharing) return;
      if (e.target.closest && e.target.closest('input, textarea')) return;
      const card = list[active];
      if (['ArrowDown', 'PageDown', 'j'].includes(e.key)) { e.preventDefault(); goTo(active + 1); }
      else if (['ArrowUp', 'PageUp', 'k'].includes(e.key)) { e.preventDefault(); goTo(active - 1); }
      else if ((e.key === ' ' || e.key === 'Enter') && card && !(e.target.closest && e.target.closest('button, a'))) {
        e.preventDefault();
        if (card.back) toggleReveal(card); else if (card.media_type === 'video') togglePlay(active);
      }
      else if ((e.key === 'm' || e.key === 'M') && card && card.media_type === 'video') setMuted(m => !m);
      else if (e.key === 'Escape') navigate('/cards');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (loadError) return (
    <div className="r-shell r-center">
      <p className="r-empty">{loadError}</p>
      <Link to="/cards" className="r-pill">← tous les paquets</Link>
    </div>
  );
  if (!deck) return <div className="r-shell r-center"><div className="g-spinner" aria-label="chargement" /></div>;

  const knownCount = deck.cards.filter(c => known.has(c.id)).length;
  const toReview = deck.cards.length - knownCount;
  const onEnd = active >= total;

  return (
    <div className="r-shell">
      <header className="r-top">
        <Link to="/cards" className="r-icon-btn" aria-label="Fermer et revenir aux paquets">{Icon.close}</Link>
        <div className="r-top-title">
          <span className="r-deck-name">{deck.title}</span>
          <span className="r-counter">{reviewMode ? 'à revoir · ' : ''}{onEnd ? 'terminé' : `${active + 1} / ${total}`}</span>
        </div>
        <span className="r-icon-btn r-ghost" aria-hidden="true" />
        <span className="r-progress" aria-hidden="true"><span style={{ transform: `scaleX(${total ? Math.min(1, (active + 1) / total) : 0})` }} /></span>
      </header>

      <div className="r-feed" ref={feedRef} onScroll={onScroll} tabIndex={-1}>
        {list.map((card, i) => (
          <Slide
            key={`${reviewMode ? 'r' : 'a'}-${card.id}`}
            card={card} index={i} total={total}
            active={i === active} near={Math.abs(i - active) <= 1}
            revealed={revealed.has(card.id)} known={known.has(card.id)}
            muted={muted} paused={paused} burst={bursts[card.id]}
            videoRef={(el) => { videoRefs.current[i] = el; }}
            onTap={() => onTap(card, i)}
            onReveal={() => toggleReveal(card)}
            onKnown={() => setKnownCard(card, !known.has(card.id))}
            onMute={() => setMuted(m => !m)}
            onShare={() => share(card, i)}
            onVideoState={(p) => { if (i === active) setPaused(p); }}
          />
        ))}

        <section className="r-slide r-end" aria-label="Fin du paquet">
          <div className="r-end-card">
            <span className="r-end-badge" aria-hidden="true">✦</span>
            <h2>{total === 0 ? 'Tout est su !' : 'Paquet terminé'}</h2>
            <p className="r-end-score"><strong>{knownCount}</strong> / {deck.cards.length}</p>
            <p className="r-end-sub">{knownCount === deck.cards.length ? 'cartes sues — bravo ✦' : 'cartes marquées « je sais »'}</p>
            <div className="r-end-actions">
              {toReview > 0 && toReview < deck.cards.length && (
                <button type="button" className="g-btn" onClick={() => restart(true)}>revoir les {toReview} carte{toReview > 1 ? 's' : ''} à revoir</button>
              )}
              <button type="button" className={toReview > 0 && toReview < deck.cards.length ? 'g-btn g-btn-soft' : 'g-btn'} onClick={() => restart(false)}>recommencer le paquet</button>
              <button type="button" className="g-btn g-btn-soft" onClick={shareDeck}>partager ce paquet</button>
              <Link to="/cards" className="g-btn g-btn-soft">autres paquets</Link>
            </div>
            <p className="r-end-tip">Astuce : touchez deux fois une carte pour la marquer « je sais ».</p>
          </div>
        </section>
      </div>

      {hint && total > 1 && !revealed.size && (
        <div className="r-hint" aria-hidden="true">
          <span className="r-hint-hand">{Icon.up}</span>
          glissez vers le haut
        </div>
      )}

      <div className="r-arrows">
        <button type="button" className="r-icon-btn" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Carte précédente">{Icon.up}</button>
        <button type="button" className="r-icon-btn" onClick={() => goTo(active + 1)} disabled={onEnd} aria-label="Carte suivante">{Icon.down}</button>
      </div>

      {sharing && <ShareSheet {...sharing} lang="fr" onClose={() => setSharing(null)} />}
    </div>
  );
}
